import moment from "moment";

import type {
    DateRange,
    DateRangeSelection,
    DateRangeValue,
    PartialDateRangeSelection,
    TransformValueFn,
} from "./types.ts";

const applyTimeNow = (
    date: Date | null | undefined,
    now: moment.Moment,
): Date | null | undefined => {
    if (!date) {
        return null;
    }

    return moment({
        year: date.getFullYear(),
        month: date.getMonth(),
        date: date.getDate(),
        hour: now.hour(),
        minute: now.minute(),
        second: now.second(),
    }).toDate();
};

export function transformDateRangeValue(
    dateRangeValue: DateRangeValue,
    dateRange?: DateRange,
): DateRange {
    switch (dateRangeValue) {
        case "custom":
            if (dateRange) {
                const now = moment();

                return {
                    // MaybeNativeDatePicker returns fixed time for the
                    // selected days. We want to set the time to now to keep
                    // this option consistent with other options.
                    startDate: applyTimeNow(dateRange.startDate, now),
                    endDate: applyTimeNow(dateRange.endDate, now),
                };
            }

            return {
                startDate: null,
                endDate: null,
            };
        case "today":
            return {
                startDate: moment().toDate(),
                endDate: moment().toDate(),
            };
        case "last-7-days":
            return {
                startDate: moment().subtract(7, "days").toDate(),
                endDate: moment().toDate(),
            };
        case "this_week":
            return {
                startDate: moment().startOf("isoWeek").toDate(),
                endDate: moment().toDate(),
            };
        case "last-30-days":
            return {
                startDate: moment().subtract(30, "days").toDate(),
                endDate: moment().toDate(),
            };
        default:
            return {
                startDate: null,
                endDate: null,
            };
    }
}

// NOTE(miguel): we want to make sure that the start/end hours in the
// day are Start: 00:00:00 End: 23:59:59. That is to properly reflect
// the general use case of due at the end of day.
export function transformDateTimeRangeValue(
    dateRangeValue: DateRangeValue,
    dateRange?: DateRange | null,
): DateRange {
    switch (dateRangeValue) {
        case "custom":
            if (dateRange) {
                return {
                    startDate:
                        dateRange.startDate &&
                        moment(dateRange.startDate).startOf("day").toDate(),
                    endDate:
                        dateRange.endDate &&
                        moment(dateRange.endDate).endOf("day").toDate(),
                };
            }

            return {
                startDate: null,
                endDate: null,
            };
        case "today":
            return {
                startDate: moment().startOf("day").toDate(),
                endDate: moment().endOf("day").toDate(),
            };
        case "last-7-days":
            return {
                startDate: moment().subtract(7, "days").startOf("day").toDate(),
                endDate: moment().endOf("day").toDate(),
            };
        case "this_week":
            return {
                startDate: moment().startOf("isoWeek").startOf("day").toDate(),
                endDate: moment().endOf("day").toDate(),
            };
        case "last-30-days":
            return {
                startDate: moment()
                    .subtract(30, "days")
                    .startOf("day")
                    .toDate(),
                endDate: moment().endOf("day").toDate(),
            };
        default:
            return {
                startDate: null,
                endDate: null,
            };
    }
}

export function baseGetSelectedDateRange(
    optionalDefault: DateRangeValue | null | undefined,
    transformValue: TransformValueFn,
): DateRangeSelection {
    const selectedValue = optionalDefault || "*";

    return {
        selectedValue,
        dateRange: transformValue(selectedValue),
    };
}

export function baseTransformDateRangeSelection(
    dateRangeSelection: DateRangeSelection | PartialDateRangeSelection,
    transformValue: TransformValueFn,
): DateRangeSelection {
    const {selectedValue, dateRange} = dateRangeSelection;
    let actualDateRange: DateRange | null | undefined;

    if (selectedValue === "custom") {
        if (dateRange) {
            actualDateRange = {
                endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
                startDate: dateRange.startDate
                    ? new Date(dateRange.startDate)
                    : null,
            };
        } else {
            actualDateRange = {
                endDate: null,
                startDate: null,
            };
        }
    } else {
        actualDateRange = transformValue(selectedValue);
    }

    return {
        selectedValue,
        dateRange: actualDateRange,
    };
}

export function getSelectedDateRange(
    priorSelection: DateRangeSelection | null,
    optionalDefault?: DateRangeValue | null,
): DateRangeSelection {
    // Support passing prior date range.
    // Simply transform value and return it.
    if (priorSelection) {
        return baseTransformDateRangeSelection(
            priorSelection,
            transformDateRangeValue,
        );
    }
    return baseGetSelectedDateRange(optionalDefault, transformDateRangeValue);
}

export function transformDateRangeSelection(
    dateRangeSelection: DateRangeSelection | PartialDateRangeSelection,
): DateRangeSelection {
    return baseTransformDateRangeSelection(
        dateRangeSelection,
        transformDateRangeValue,
    );
}

interface DateRangeToString {
    startDate: string | null | undefined;
    endDate: string | null | undefined;
}

function pad(n: number): string {
    if (n < 10) {
        return `0${n}`;
    }

    return `${n}`;
}

/**
 * TODO(miguel): We have a mixed bag of DateTime and Date types in
 * our reports.  They should all be Dates.  Because of this discrepancy
 * we have some oddities when dealing with DateTime that are expected
 * to be UTC and some that are expected to in local time.  These functions
 * below are to help us deal with that for now, but we are going to
 * clean this up in short order.
 * JIRA: https://khanacademy.atlassian.net/browse/DIST-678
 */

/**
 * This function will build an ISO date formatted string but
 * will use local time instead of UTC. This is needed for
 * reports that need the date/time based on local time in order
 * to build aggregated reporting data that is relevant to the
 * user seeing the reports.
 *
 * For more information, check out
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toISOString#Polyfill
 */
function toLocalISOString(date?: Date | null) {
    if (!date) {
        return null;
    }

    // ISO Format
    // 2019-07-10T14:18:43.711Z
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate(),
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds(),
    )}.000Z`;
}

/**
 * This function takes in a date range object and returns a date
 * range object where startDate and endDate are ISO date formatted
 * strings, but with local time instead of UTC. This allows us to
 * gracefully handle date ranges as needed for reports where we
 * need to aggragate data based on the user's local time of so that
 * reports can generate data that is relevant to the user.
 */
export function formatDateRangeAsLocalISODateRange(
    dateRange?: DateRange | null,
): DateRangeToString {
    return {
        startDate: toLocalISOString(dateRange?.startDate),
        endDate: toLocalISOString(dateRange?.endDate),
    };
}

function toLocalDateString(date?: Date | null) {
    if (!date) {
        return null;
    }

    // Date Format
    // 2019-07-10
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate(),
    )}`;
}

export function formatDateRangeAsLocalDateRange(
    dateRange?: DateRange | null,
): DateRangeToString {
    return {
        startDate: toLocalDateString(dateRange && dateRange.startDate),
        endDate: toLocalDateString(dateRange && dateRange.endDate),
    };
}
